import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ComposedChart, Area, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import {
  Calendar, Download, ChevronDown, RefreshCw, AlertCircle,
  Thermometer, Droplets, Wind, CloudRain, Sun, Gauge,
  BarChart2, TrendingUp, ArrowUp, ArrowDown, Minus, Activity,
  Check, Leaf, Zap, ChevronRight,
} from 'lucide-react';
import {
  fetchWeatherData, calculateStatistics, formatDateForAPI,
  getDateRanges, aggregateDataByHour, aggregateDataByDay,
} from '../../services/weatherDataService';
import { exportToCSV, exportToJSON, exportToExcelCSV, generateFilename } from '../../services/exportService';
import { fetchSoilDevices, fetchSoilReadings } from '../../services/soilService';

// ─── Weather parameter definitions ────────────────────────────────────────────
const PARAMS = [
  { key: 'temperature', label: 'Temperature', unit: '°C',   icon: Thermometer, col: { hex: '#ef4444', soft: '#fef2f2', border: '#fca5a5', dark: '#b91c1c', muted: '#fee2e2' }, statKey: 'temperature', dataKey: 'temperature', precision: 1 },
  { key: 'humidity',    label: 'Humidity',    unit: '%',    icon: Droplets,    col: { hex: '#3b82f6', soft: '#eff6ff', border: '#93c5fd', dark: '#1d4ed8', muted: '#dbeafe' }, statKey: 'humidity',    dataKey: 'humidity',    precision: 0 },
  { key: 'windSpeed',   label: 'Wind Speed',  unit: ' m/s', icon: Wind,        col: { hex: '#10b981', soft: '#ecfdf5', border: '#6ee7b7', dark: '#065f46', muted: '#d1fae5' }, statKey: 'windSpeed',   dataKey: 'windSpeed',   precision: 1 },
  { key: 'pressure',    label: 'Pressure',    unit: ' hPa', icon: Gauge,       col: { hex: '#8b5cf6', soft: '#f5f3ff', border: '#c4b5fd', dark: '#5b21b6', muted: '#ede9fe' }, statKey: 'pressure',    dataKey: 'pressure',    precision: 1 },
  { key: 'rainfall',    label: 'Rainfall',    unit: ' mm',  icon: CloudRain,   col: { hex: '#0ea5e9', soft: '#f0f9ff', border: '#7dd3fc', dark: '#0369a1', muted: '#e0f2fe' }, statKey: 'rainfall',    dataKey: 'rainfall',    precision: 2 },
  { key: 'uvIndex',     label: 'UV Index',    unit: '',     icon: Sun,         col: { hex: '#f59e0b', soft: '#fffbeb', border: '#fcd34d', dark: '#92400e', muted: '#fef3c7' }, statKey: 'uvIndex',     dataKey: 'uvIndex',     precision: 1 },
];

// ─── Soil parameter definitions ───────────────────────────────────────────────
const SOIL_PARAMS = [
  { key: 'hum',   label: 'Moisture',     unit: '%',       icon: Droplets,    col: { hex: '#3b82f6', soft: '#eff6ff', border: '#93c5fd', dark: '#1d4ed8', muted: '#dbeafe' }, precision: 0, dataKey: 'hum'   },
  { key: 'temp',  label: 'Soil Temp',    unit: '°C',      icon: Thermometer, col: { hex: '#ef4444', soft: '#fef2f2', border: '#fca5a5', dark: '#b91c1c', muted: '#fee2e2' }, precision: 1, dataKey: 'temp'  },
  { key: 'ph',    label: 'pH Level',     unit: '',        icon: Activity,    col: { hex: '#10b981', soft: '#ecfdf5', border: '#6ee7b7', dark: '#065f46', muted: '#d1fae5' }, precision: 1, dataKey: 'ph'    },
  { key: 'ec',    label: 'Conductivity', unit: ' µS/cm',  icon: Zap,         col: { hex: '#8b5cf6', soft: '#f5f3ff', border: '#c4b5fd', dark: '#5b21b6', muted: '#ede9fe' }, precision: 0, dataKey: 'ec'    },
  { key: 'nitro', label: 'Nitrogen',     unit: ' mg/kg',  icon: Leaf,        col: { hex: '#84cc16', soft: '#f7fee7', border: '#bef264', dark: '#3f6212', muted: '#ecfccb' }, precision: 0, dataKey: 'nitro' },
  { key: 'phos',  label: 'Phosphorus',   unit: ' mg/kg',  icon: Leaf,        col: { hex: '#f97316', soft: '#fff7ed', border: '#fed7aa', dark: '#9a3412', muted: '#ffedd5' }, precision: 0, dataKey: 'phos'  },
  { key: 'pota',  label: 'Potassium',    unit: ' mg/kg',  icon: Leaf,        col: { hex: '#eab308', soft: '#fefce8', border: '#fde68a', dark: '#92400e', muted: '#fef9c3' }, precision: 0, dataKey: 'pota'  },
];

const SOIL_KEYS = ['hum', 'temp', 'ph', 'ec', 'nitro', 'phos', 'pota'];

const CHART_TYPES = [{ key: 'area', label: 'Area' }, { key: 'line', label: 'Line' }, { key: 'bar', label: 'Bar' }];

const RANGE_LABELS = {
  today: 'Today', yesterday: 'Yesterday',
  lastWeek: 'Last 7 Days', lastMonth: 'Last 30 Days',
  lastYear: 'Last Year', custom: 'Custom Range',
};

const RANGES = [
  { k: 'today', l: 'Today' }, { k: 'yesterday', l: 'Yesterday' },
  { k: 'lastWeek', l: '7 Days' }, { k: 'lastMonth', l: '30 Days' },
  { k: 'lastYear', l: '1 Year' }, { k: 'custom', l: 'Custom' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const aggregateToMonthly = (dailyData) => {
  const groups = {};
  dailyData.forEach(item => {
    if (!item.timestamp) return;
    const d = new Date(item.timestamp);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!groups[key]) groups[key] = { ts: new Date(d.getFullYear(), d.getMonth(), 1), items: [] };
    groups[key].items.push(item);
  });
  const FIELDS = ['temperature', 'humidity', 'windSpeed', 'pressure', 'rainfall', 'uvIndex'];
  return Object.values(groups).sort((a, b) => a.ts - b.ts).map(({ ts, items }) => {
    const avg = {};
    FIELDS.forEach(f => {
      const vals = items.map(i => i[f]).filter(v => v != null && !isNaN(v));
      avg[f] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    });
    return { timestamp: ts, time: ts.toLocaleDateString([], { month: 'short', year: '2-digit' }), ...avg };
  });
};

const getStatForParam = (p, st) => {
  if (!st) return null;
  if (p.key === 'rainfall') return { current: st.rainfall.current, min: st.rainfall.min, max: st.rainfall.max, average: st.rainfall.average };
  return st[p.statKey] ?? null;
};

const trimChartData = (data, activeKeys, allParams) => {
  const dataKeys = allParams.filter(p => activeKeys.includes(p.key)).map(p => p.dataKey);
  const hasData = d => dataKeys.some(k => d[k] != null);
  const first = data.findIndex(hasData);
  if (first === -1) return data;
  let last = data.length - 1;
  while (last > first && !hasData(data[last])) last--;
  return data.slice(first, last + 1);
};

const addNormalizedFields = (data, activeParams, statFn) => data.map(point => {
  const out = { ...point };
  activeParams.forEach(p => {
    const stat = statFn(p);
    const min = stat?.min ?? 0;
    const max = stat?.max ?? 100;
    const val = point[p.dataKey];
    out[`_n_${p.key}`] = val != null && max !== min
      ? Math.round(((val - min) / (max - min)) * 100 * 10) / 10 : null;
  });
  return out;
});

// Soil helpers
const formatSoilForChart = (readings) =>
  [...readings].reverse().map(r => ({
    timestamp: new Date(r.receivedAt),
    time: new Date(r.receivedAt).toLocaleDateString([], { month: 'short', day: 'numeric' }),
    ...Object.fromEntries(SOIL_KEYS.map(k => [k, r.soilData?.[k] ?? null])),
  }));

const aggregateSoilByPeriod = (formatted, period) => {
  const isHourly = period === 'hour';
  const groups = {};
  formatted.forEach(r => {
    const d = new Date(r.timestamp);
    if (isHourly) d.setMinutes(0, 0, 0); else d.setHours(0, 0, 0, 0);
    const key = d.getTime();
    if (!groups[key]) groups[key] = { ts: d, items: [] };
    groups[key].items.push(r);
  });
  return Object.values(groups).sort((a, b) => a.ts - b.ts).map(({ ts, items }) => {
    const avg = {};
    SOIL_KEYS.forEach(k => {
      const vals = items.map(i => i[k]).filter(v => v != null && !isNaN(v));
      avg[k] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    });
    return {
      timestamp: ts,
      time: isHourly
        ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : ts.toLocaleDateString([], { month: 'short', day: 'numeric' }),
      ...avg,
    };
  });
};

const calculateSoilStats = (readings) => {
  const latest = readings[0]?.soilData || {};
  const result = {};
  SOIL_KEYS.forEach(k => {
    const vals = readings.map(r => r.soilData?.[k]).filter(v => v != null && !isNaN(v));
    result[k] = vals.length > 0 ? {
      current: latest[k] ?? null,
      min: Math.min(...vals),
      max: Math.max(...vals),
      average: vals.reduce((s, v) => s + v, 0) / vals.length,
    } : null;
  });
  return result;
};

const getSoilStartDate = (dateRange, customFrom) => {
  switch (dateRange) {
    case 'today': { const d = new Date(); d.setHours(0,0,0,0); return d; }
    case 'yesterday': { const d = new Date(); d.setDate(d.getDate()-1); d.setHours(0,0,0,0); return d; }
    case 'lastWeek':  return new Date(Date.now() - 7  * 86400000);
    case 'lastMonth': return new Date(Date.now() - 30 * 86400000);
    case 'lastYear':  return new Date(Date.now() - 365 * 86400000);
    case 'custom':    return customFrom ? new Date(customFrom) : new Date(Date.now() - 7*86400000);
    default:          return new Date(Date.now() - 7*86400000);
  }
};

// ─── Multi-param Tooltip ──────────────────────────────────────────────────────
const MultiTooltip = ({ active, payload, label, activeParams, normalize }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: '10px 14px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', fontSize: 12, minWidth: 170 }}>
      <p style={{ color: '#6b7280', marginBottom: 8, fontWeight: 700, borderBottom: '1px solid #f3f4f6', paddingBottom: 6 }}>{label}</p>
      {activeParams.map(p => {
        const dk = normalize ? `_n_${p.key}` : p.dataKey;
        const entry = payload.find(e => e.dataKey === dk);
        const raw = entry?.payload?.[p.dataKey];
        return (
          <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.col.hex, flexShrink: 0 }} />
            <span style={{ color: '#374151', flex: 1 }}>{p.label}</span>
            <span style={{ fontWeight: 800, color: '#111827' }}>
              {raw != null ? `${Number(raw).toFixed(p.precision)}${p.unit}` : '—'}
              {normalize && entry?.value != null && <span style={{ color: '#9ca3af', fontWeight: 500, fontSize: 10 }}> ({Number(entry.value).toFixed(0)}%)</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ param, stat, isSelected, onToggle }) => {
  const { icon: Icon, label, unit, col, precision } = param;
  const fmt = v => v != null && !isNaN(v) ? Number(v).toFixed(precision) : '—';
  const current = stat?.current ?? null;
  const min = stat?.min ?? null;
  const max = stat?.max ?? null;
  const avg = stat?.average ?? null;
  const pct = max != null && min != null && max !== min && current != null
    ? Math.max(4, Math.min(96, ((current - min) / (max - min)) * 100)) : null;
  const trend = avg != null && current != null && max != null && min != null && max !== min
    ? ((current - avg) / (max - min)) * 100 : null;

  return (
    <div onClick={onToggle} style={{
      position: 'relative', background: isSelected ? col.soft : '#fff',
      borderRadius: 14,
      border: isSelected ? `2px solid ${col.hex}` : '1.5px solid #f1f5f9',
      boxShadow: isSelected ? `0 0 0 3px ${col.hex}22, 0 4px 16px rgba(0,0,0,0.06)` : '0 2px 8px rgba(0,0,0,0.03)',
      padding: '13px 13px 11px', display: 'flex', flexDirection: 'column', gap: 8,
      cursor: 'pointer', transition: 'all 0.18s',
    }}>
      {isSelected && (
        <div style={{ position: 'absolute', top: 8, right: 8, width: 18, height: 18, borderRadius: '50%', background: col.hex, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Check style={{ width: 10, height: 10, color: '#fff' }} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: col.soft, border: `1px solid ${col.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon style={{ width: 14, height: 14, color: col.hex }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {trend != null && (trend > 8 ? <ArrowUp style={{ width: 10, height: 10, color: col.hex }} /> : trend < -8 ? <ArrowDown style={{ width: 10, height: 10, color: '#9ca3af' }} /> : <Minus style={{ width: 10, height: 10, color: '#d1d5db' }} />)}
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: col.dark }}>{label}</span>
        </div>
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <span style={{ fontSize: 26, fontWeight: 900, color: '#111827', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{fmt(current)}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af' }}>{unit}</span>
        </div>
        <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>Current reading</p>
      </div>
      {pct != null && (
        <div style={{ height: 3, borderRadius: 999, background: col.muted }}>
          <div style={{ height: '100%', borderRadius: 999, background: col.hex, width: `${pct}%`, transition: 'width 0.5s ease' }} />
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
        {[['Min', min], ['Avg', avg], ['Max', max]].map(([lbl, val]) => (
          <div key={lbl} style={{ textAlign: 'center', background: isSelected ? '#fff' : col.soft, borderRadius: 6, padding: '4px 2px', border: `1px solid ${col.border}` }}>
            <p style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: col.dark, marginBottom: 1 }}>{lbl}</p>
            <p style={{ fontSize: 11, fontWeight: 900, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>{fmt(val)}{unit}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Chart Panel (generic — works for weather and soil) ───────────────────────
const ChartPanel = ({
  chartData, activeParams, allParams, chartType, setChartType,
  normalize, setNormalize, statFn, selectedKeys, onToggleParam,
  dateRange, chartHeight = 420, title, subtitle,
}) => {
  const col = activeParams[0]?.col ?? PARAMS[0].col;

  const processedData = useMemo(() => {
    const trimmed = trimChartData(chartData, selectedKeys, allParams);
    return normalize ? addNormalizedFields(trimmed, activeParams, statFn) : trimmed;
  }, [chartData, selectedKeys, normalize, activeParams, statFn, allParams]); // eslint-disable-line

  const renderSeries = () => activeParams.map(p => {
    const dk = normalize ? `_n_${p.key}` : p.dataKey;
    if (chartType === 'bar')  return <Bar  key={p.key} dataKey={dk} name={p.label} fill={p.col.hex} radius={[4,4,0,0]} maxBarSize={20} />;
    if (chartType === 'line') return <Line key={p.key} type="monotone" dataKey={dk} name={p.label} stroke={p.col.hex} strokeWidth={2.5} dot={false} activeDot={{ r: 5, strokeWidth: 2 }} connectNulls />;
    return <Area key={p.key} type="monotone" dataKey={dk} name={p.label} stroke={p.col.hex} fill={`url(#ga_${p.key})`} fillOpacity={1} strokeWidth={2.5} dot={false} activeDot={{ r: 5, strokeWidth: 2 }} connectNulls />;
  });

  const xTickInterval = processedData.length > 180 ? Math.floor(processedData.length / 12) - 1
    : processedData.length > 60 ? Math.floor(processedData.length / 8) - 1
    : processedData.length > 28 ? 4 : processedData.length > 14 ? 1 : 0;

  const axisProps = { stroke: '#f1f5f9', fontSize: 11, tickLine: false, axisLine: false, fill: '#6b7280' };
  const ChartComp = chartType === 'bar' ? BarChart : ComposedChart;

  const avgStat = activeParams.length === 1 ? statFn(activeParams[0]) : null;
  const avgLabel = avgStat != null
    ? `avg ${Number(avgStat.average ?? 0).toFixed(activeParams[0].precision)}${activeParams[0].unit}` : null;

  return (
    <div style={{ background: '#fff', borderRadius: 18, border: '1.5px solid #f1f5f9', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Card header */}
      <div style={{ borderLeft: `4px solid ${col.hex}`, padding: '16px 20px 12px', borderBottom: '1px solid #f8fafc' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <div>
            <p style={{ fontSize: 15, fontWeight: 800, color: '#1f2937', margin: 0 }}>
              {title || (activeParams.length === 1 ? activeParams[0].label : `${activeParams.length} Parameters`)}
            </p>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 0' }}>
              {subtitle || `${RANGE_LABELS[dateRange]} · ${processedData.filter(d => activeParams.some(p => d[p.dataKey] != null)).length} data points`}
            </p>
          </div>
          {avgLabel && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: col.soft, border: `1px solid ${col.border}`, borderRadius: 20, padding: '4px 10px', flexShrink: 0 }}>
              <Activity style={{ width: 11, height: 11, color: col.hex }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: col.dark }}>{avgLabel}</span>
            </div>
          )}
        </div>

        {/* Parameter pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 2 }}>Parameters</span>
          {allParams.map(p => {
            const active = selectedKeys.includes(p.key);
            return (
              <button key={p.key} onClick={() => onToggleParam(p.key)} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 20, border: `1.5px solid ${active ? p.col.hex : p.col.border}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
                background: active ? p.col.hex : '#f9fafb',
                color: active ? '#fff' : p.col.dark,
                boxShadow: active ? `0 2px 8px ${p.col.hex}44` : 'none',
              }}>
                <p.icon style={{ width: 10, height: 10 }} />{p.label}
                {active && <Check style={{ width: 9, height: 9 }} />}
              </button>
            );
          })}
        </div>

        {/* Chart type + normalize */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 2 }}>Chart</span>
          {CHART_TYPES.map(({ key, label }) => (
            <button key={key} onClick={() => setChartType(key)} style={{
              padding: '3px 10px', borderRadius: 20, border: '1.5px solid', cursor: 'pointer', fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
              background: chartType === key ? '#1f2937' : '#f9fafb',
              borderColor: chartType === key ? '#1f2937' : '#e5e7eb',
              color: chartType === key ? '#fff' : '#6b7280',
            }}>{label}</button>
          ))}
          {selectedKeys.length > 1 && (
            <button onClick={() => setNormalize(v => !v)} style={{
              display: 'flex', alignItems: 'center', gap: 5, marginLeft: 4,
              padding: '3px 10px', borderRadius: 20, border: '1.5px solid', cursor: 'pointer', fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
              background: normalize ? '#7c3aed' : '#f9fafb',
              borderColor: normalize ? '#7c3aed' : '#e5e7eb',
              color: normalize ? '#fff' : '#6b7280',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: normalize ? '#fff' : '#9ca3af' }} />
              Normalize {normalize && <span style={{ fontSize: 9, opacity: 0.8 }}>(0–100%)</span>}
            </button>
          )}
        </div>
      </div>

      {/* Chart */}
      <div style={{ flex: 1, padding: '8px 4px 16px' }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ChartComp data={processedData} margin={{ top: 12, right: 20, left: 0, bottom: 4 }}>
            <defs>
              {activeParams.map(p => (
                <linearGradient key={p.key} id={`ga_${p.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor={p.col.hex} stopOpacity={0.30} />
                  <stop offset="90%" stopColor={p.col.hex} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="time" {...axisProps} interval={xTickInterval}
              angle={processedData.length > 60 ? -35 : 0}
              textAnchor={processedData.length > 60 ? 'end' : 'middle'}
              height={processedData.length > 60 ? 50 : 28} />
            <YAxis {...axisProps} width={50} tickFormatter={v => normalize ? `${v}%` : `${v}`} />
            <Tooltip content={<MultiTooltip activeParams={activeParams} normalize={normalize} />} />
            {activeParams.length > 1 && (
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                formatter={value => {
                  const p = allParams.find(x => x.label === value);
                  return <span style={{ color: p?.col.dark ?? '#374151', fontWeight: 700 }}>{value}</span>;
                }} />
            )}
            {activeParams.length === 1 && (() => {
              const avg = statFn(activeParams[0])?.average;
              const y = normalize ? 50 : avg;
              return avg != null ? (
                <ReferenceLine y={y} stroke={col.hex} strokeDasharray="5 5" strokeOpacity={0.4}
                  label={{ value: 'avg', position: 'insideTopRight', fontSize: 10, fill: col.hex, opacity: 0.7 }} />
              ) : null;
            })()}
            {renderSeries()}
          </ChartComp>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─── Weather Insights Strip ───────────────────────────────────────────────────
const WeatherInsights = ({ st, count }) => {
  if (!st) return null;
  const fmt1 = v => v != null && !isNaN(v) ? Number(v).toFixed(1) : '—';
  const items = [
    { icon: Thermometer, label: 'Peak Temp',     value: `${fmt1(st.temperature.max)}°C`,         col: PARAMS[0].col },
    { icon: Thermometer, label: 'Low Temp',      value: `${fmt1(st.temperature.min)}°C`,         col: PARAMS[0].col },
    { icon: Droplets,    label: 'Peak Humidity', value: `${fmt1(st.humidity.max)}%`,             col: PARAMS[1].col },
    { icon: Wind,        label: 'Max Wind',      value: `${fmt1(st.windSpeed?.max)} m/s`,        col: PARAMS[2].col },
    { icon: CloudRain,   label: 'Total Rain',    value: `${fmt1(st.rainfall.total)} mm`,         col: PARAMS[4].col },
    { icon: Sun,         label: 'Peak UV',       value: `${fmt1(st.uvIndex?.max)}`,              col: PARAMS[5].col },
  ];
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '14px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <TrendingUp style={{ width: 13, height: 13, color: '#10b981' }} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#374151' }}>Period Highlights</span>
        <span style={{ fontSize: 10, color: '#d1d5db', marginLeft: 2 }}>{count?.toLocaleString()} readings</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map(({ icon: Icon, label, value, col }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, background: col.soft, border: `1px solid ${col.border}`, borderRadius: 20, padding: '4px 11px' }}>
            <Icon style={{ width: 11, height: 11, color: col.hex, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#6b7280' }}>{label}:</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#111827' }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Soil Insights Strip ──────────────────────────────────────────────────────
const SoilInsights = ({ st, count }) => {
  if (!st) return null;
  const fmt = (v, d = 1) => v != null && !isNaN(v) ? Number(v).toFixed(d) : '—';

  const getMoistureStatus = (v) => {
    if (v == null) return null;
    if (v < 20) return { label: 'Very Dry', color: '#ef4444' };
    if (v < 40) return { label: 'Dry', color: '#f97316' };
    if (v <= 70) return { label: 'Optimal', color: '#10b981' };
    return { label: 'Wet', color: '#3b82f6' };
  };
  const getPhStatus = (v) => {
    if (v == null) return null;
    if (v < 5.5) return { label: 'Very Acidic', color: '#ef4444' };
    if (v < 6.0) return { label: 'Acidic', color: '#f97316' };
    if (v <= 7.5) return { label: 'Optimal', color: '#10b981' };
    return { label: 'Alkaline', color: '#8b5cf6' };
  };

  const hum = st.hum?.current;
  const ph  = st.ph?.current;
  const moistureStatus = getMoistureStatus(hum);
  const phStatus = getPhStatus(ph);

  const items = [
    { icon: Droplets,    label: 'Avg Moisture', value: `${fmt(st.hum?.average, 0)}%`,        col: SOIL_PARAMS[0].col },
    { icon: Thermometer, label: 'Avg Soil Temp', value: `${fmt(st.temp?.average)}°C`,         col: SOIL_PARAMS[1].col },
    { icon: Activity,    label: 'Avg pH',        value: `${fmt(st.ph?.average)}`,             col: SOIL_PARAMS[2].col },
    { icon: Zap,         label: 'Avg EC',        value: `${fmt(st.ec?.average, 0)} µS/cm`,    col: SOIL_PARAMS[3].col },
    { icon: Leaf,        label: 'Max Nitrogen',  value: `${fmt(st.nitro?.max, 0)} mg/kg`,     col: SOIL_PARAMS[4].col },
  ];

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '14px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Leaf style={{ width: 13, height: 13, color: '#10b981' }} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#374151' }}>Soil Insights</span>
        <span style={{ fontSize: 10, color: '#d1d5db', marginLeft: 2 }}>{count?.toLocaleString()} readings</span>
        {moistureStatus && (
          <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, background: `${moistureStatus.color}18`, color: moistureStatus.color, padding: '2px 8px', borderRadius: 10 }}>
            Moisture: {moistureStatus.label}
          </span>
        )}
        {phStatus && (
          <span style={{ fontSize: 10, fontWeight: 700, background: `${phStatus.color}18`, color: phStatus.color, padding: '2px 8px', borderRadius: 10 }}>
            pH: {phStatus.label}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map(({ icon: Icon, label, value, col }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, background: col.soft, border: `1px solid ${col.border}`, borderRadius: 20, padding: '4px 11px' }}>
            <Icon style={{ width: 11, height: 11, color: col.hex, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#6b7280' }}>{label}:</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#111827' }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Empty / Loading / Error states ──────────────────────────────────────────
const EmptyState = ({ icon: Icon = BarChart2, title, sub, onRetry, accentColor = '#10b981' }) => (
  <div style={{ background: '#fff', borderRadius: 18, border: '1.5px solid #f1f5f9', padding: '70px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
    <div style={{ width: 52, height: 52, borderRadius: 16, background: `${accentColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon style={{ width: 22, height: 22, color: accentColor }} />
    </div>
    <p style={{ fontSize: 15, fontWeight: 700, color: '#374151', margin: 0 }}>{title}</p>
    {sub && <p style={{ fontSize: 12, color: '#9ca3af', margin: 0, textAlign: 'center', maxWidth: 340 }}>{sub}</p>}
    {onRetry && <button onClick={onRetry} style={{ marginTop: 4, padding: '7px 18px', background: accentColor, color: '#fff', border: 'none', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Retry</button>}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const WeatherAnalyticsDashboard = ({ stationId }) => {
  // ── Shared state ─────────────────────────────────────────────────────────
  const [section,      setSection]      = useState('weather'); // 'weather' | 'soil'
  const [dateRange,    setDateRange]    = useState('lastWeek');
  const [customFrom,   setCustomFrom]   = useState('');
  const [customTo,     setCustomTo]     = useState('');
  const [showExport,   setShowExport]   = useState(false);

  // ── Weather state ─────────────────────────────────────────────────────────
  const [weatherData,    setWeatherData]    = useState([]);
  const [statistics,     setStatistics]     = useState(null);
  const [wLoading,       setWLoading]       = useState(false);
  const [wError,         setWError]         = useState(null);
  const [selectedKeys,   setSelectedKeys]   = useState(['temperature']);
  const [chartType,      setChartType]      = useState('area');
  const [normalize,      setNormalize]      = useState(false);

  // ── Soil state ────────────────────────────────────────────────────────────
  const [soilDevices,      setSoilDevices]      = useState([]);
  const [selectedSoilDev,  setSelectedSoilDev]  = useState(null);
  const [soilReadings,     setSoilReadings]      = useState([]);
  const [soilStats,        setSoilStats]         = useState(null);
  const [soilLoading,      setSoilLoading]       = useState(false);
  const [soilError,        setSoilError]         = useState(null);
  const [selectedSoilKeys, setSelectedSoilKeys]  = useState(['hum', 'temp']);
  const [soilChartType,    setSoilChartType]     = useState('area');
  const [soilNormalize,    setSoilNormalize]     = useState(false);

  // ── Load weather ──────────────────────────────────────────────────────────
  const loadWeatherData = useCallback(async () => {
    setWLoading(true); setWError(null);
    try {
      const ranges = getDateRanges();
      let fromDate, toDate;
      if (dateRange === 'custom') {
        fromDate = customFrom ? new Date(customFrom) : null;
        toDate   = customTo   ? new Date(customTo)   : null;
      } else {
        const r = ranges[dateRange]; fromDate = r.from; toDate = r.to;
      }
      let allData = [], page = 1;
      while (page <= 10 && allData.length < 5000) {
        const res = await fetchWeatherData({ stationId, fromDateTime: formatDateForAPI(fromDate), toDateTime: formatDateForAPI(toDate), page, pageSize: 500 });
        if (!res.data?.length) break;
        allData.push(...res.data);
        if (!res.has_next) break;
        page++;
      }
      setWeatherData(allData);
      setStatistics(calculateStatistics(allData));
    } catch {
      setWError('Failed to load weather data. Please try again.');
    } finally {
      setWLoading(false);
    }
  }, [stationId, dateRange, customFrom, customTo]); // eslint-disable-line

  // ── Load soil devices ─────────────────────────────────────────────────────
  useEffect(() => {
    fetchSoilDevices().then(devs => {
      setSoilDevices(devs);
      if (devs.length > 0 && !selectedSoilDev) setSelectedSoilDev(devs[0]);
    }).catch(() => {});
  }, []); // eslint-disable-line

  // ── Load soil readings ────────────────────────────────────────────────────
  const loadSoilData = useCallback(async () => {
    if (!selectedSoilDev) return;
    setSoilLoading(true); setSoilError(null);
    try {
      const startDate = getSoilStartDate(dateRange, customFrom);
      const readings  = await fetchSoilReadings(selectedSoilDev.deviceId, { startDate: startDate.toISOString(), limit: 500 });
      setSoilReadings(readings);
      setSoilStats(readings.length > 0 ? calculateSoilStats(readings) : null);
    } catch {
      setSoilError('Failed to load soil data. Please try again.');
    } finally {
      setSoilLoading(false);
    }
  }, [selectedSoilDev, dateRange, customFrom]); // eslint-disable-line

  useEffect(() => { if (section === 'weather') loadWeatherData(); }, [section, dateRange]); // eslint-disable-line
  useEffect(() => { if (section === 'soil' && selectedSoilDev) loadSoilData(); }, [section, selectedSoilDev, dateRange]); // eslint-disable-line

  useEffect(() => {
    const close = e => { if (!e.target.closest?.('.export-wrap')) setShowExport(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    const hasPressure = selectedKeys.includes('pressure');
    const hasOthers   = selectedKeys.some(k => k !== 'pressure');
    if (hasPressure && hasOthers && selectedKeys.length > 1) setNormalize(true);
    else if (selectedKeys.length === 1) setNormalize(false);
  }, [selectedKeys]);

  useEffect(() => {
    if (selectedSoilKeys.length === 1) setSoilNormalize(false);
  }, [selectedSoilKeys]);

  // ── Param toggle helpers ──────────────────────────────────────────────────
  const toggleParam = key =>
    setSelectedKeys(prev => prev.includes(key) ? (prev.length > 1 ? prev.filter(k => k !== key) : prev) : [...prev, key]);

  const toggleSoilParam = key =>
    setSelectedSoilKeys(prev => prev.includes(key) ? (prev.length > 1 ? prev.filter(k => k !== key) : prev) : [...prev, key]);

  // ── Chart data getters ────────────────────────────────────────────────────
  const getWeatherChartData = () => {
    if (!weatherData.length) return [];
    switch (dateRange) {
      case 'today': case 'yesterday': return aggregateDataByHour(weatherData, 24);
      case 'lastWeek':  return aggregateDataByDay(weatherData, 7);
      case 'lastMonth': return aggregateDataByDay(weatherData, 30);
      case 'lastYear':  return aggregateToMonthly(aggregateDataByDay(weatherData, 365));
      case 'custom': {
        if (customFrom && customTo) {
          const days = (new Date(customTo) - new Date(customFrom)) / 86400000;
          return days <= 2 ? aggregateDataByHour(weatherData, Math.round(days * 24)) : aggregateDataByDay(weatherData, Math.ceil(days));
        }
        return aggregateDataByDay(weatherData, 7);
      }
      default: return aggregateDataByDay(weatherData, 7);
    }
  };

  const getSoilChartData = () => {
    if (!soilReadings.length) return [];
    const formatted = formatSoilForChart(soilReadings);
    const isHourly = ['today', 'yesterday'].includes(dateRange);
    return aggregateSoilByPeriod(formatted, isHourly ? 'hour' : 'day');
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = fmt => {
    const data = section === 'weather' ? weatherData : soilReadings;
    if (!data.length) { alert('No data to export.'); return; }
    const ranges = getDateRanges();
    let fromDate, toDate;
    if (dateRange === 'custom') { fromDate = customFrom ? new Date(customFrom) : null; toDate = customTo ? new Date(customTo) : null; }
    else { const r = ranges[dateRange]; fromDate = r.from; toDate = r.to; }
    const label = section === 'soil' ? `soil_${selectedSoilDev?.deviceId || 'all'}` : `station_${stationId || 'all'}`;
    const fn = generateFilename(label, fromDate, toDate, fmt === 'json' ? 'json' : 'csv');
    try {
      if (fmt === 'csv') exportToCSV(data, fn);
      else if (fmt === 'excel') exportToExcelCSV(data, fn);
      else exportToJSON(data, fn);
      setShowExport(false);
    } catch (e) { alert('Export failed: ' + e.message); }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const weatherChartData = getWeatherChartData();
  const soilChartData    = getSoilChartData();
  const weatherParams    = PARAMS.filter(p => selectedKeys.includes(p.key));
  const soilParams       = SOIL_PARAMS.filter(p => selectedSoilKeys.includes(p.key));

  const weatherStatFn = p => getStatForParam(p, statistics);
  const soilStatFn    = p => soilStats?.[p.key] ?? null;

  const weatherChartH = { today: 360, yesterday: 360, lastWeek: 420, lastMonth: 460, lastYear: 500, custom: 440 }[dateRange] ?? 420;
  const soilChartH    = 400;

  const handleCustomApply = () => { if (customFrom && customTo) { setDateRange('custom'); } };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '14px 14px 48px', display: 'flex', flexDirection: 'column', gap: 16 }} className="analytics-outer">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div style={{ borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 28px rgba(0,0,0,0.14)' }}>
          <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #064e3b 60%, #0f172a 100%)', padding: '20px 24px 0' }}>

            {/* Top row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BarChart2 style={{ width: 20, height: 20, color: '#34d399' }} />
                </div>
                <div>
                  <h1 style={{ fontSize: 19, fontWeight: 900, color: '#fff', margin: 0 }}>Analytics Dashboard</h1>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#6ee7b7' }}>{stationId ? `Station ···${stationId.slice(-8)}` : 'All Stations'}</span>
                    {section === 'weather' && weatherData.length > 0 && (
                      <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.12)', color: '#d1d5db', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>{weatherData.length.toLocaleString()} readings</span>
                    )}
                    {section === 'soil' && soilReadings.length > 0 && (
                      <span style={{ fontSize: 11, background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>{soilReadings.length} soil readings</span>
                    )}
                    <span style={{ fontSize: 11, background: 'rgba(16,185,129,0.2)', color: '#6ee7b7', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>{RANGE_LABELS[dateRange]}</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {(wLoading || soilLoading) && <RefreshCw style={{ width: 14, height: 14, color: 'rgba(255,255,255,0.5)', animation: 'spin 1s linear infinite' }} />}
                <button
                  onClick={() => section === 'weather' ? loadWeatherData() : loadSoilData()}
                  disabled={wLoading || soilLoading}
                  style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: (wLoading || soilLoading) ? 0.5 : 1 }}
                >
                  <RefreshCw style={{ width: 13, height: 13, color: '#fff' }} />
                </button>
                <div className="export-wrap" style={{ position: 'relative' }}>
                  <button onClick={() => setShowExport(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', color: '#065f46', padding: '7px 13px', borderRadius: 10, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    <Download style={{ width: 12, height: 12 }} /> Export <ChevronDown style={{ width: 10, height: 10, transform: showExport ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </button>
                  {showExport && (
                    <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 190, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.14)', zIndex: 50, overflow: 'hidden' }}>
                      {[{ fmt: 'csv', label: 'CSV', sub: 'Comma-separated', color: '#10b981' }, { fmt: 'excel', label: 'Excel CSV', sub: 'Excel-compatible', color: '#3b82f6' }, { fmt: 'json', label: 'JSON', sub: 'Raw JSON format', color: '#8b5cf6' }].map(({ fmt, label, sub, color }) => (
                        <button key={fmt} onClick={() => handleExport(fmt)} style={{ width: '100%', padding: '9px 13px', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f9fafb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9 }}>
                          <Download style={{ width: 12, height: 12, color, flexShrink: 0 }} />
                          <div><p style={{ fontSize: 12, fontWeight: 700, color: '#111827', margin: 0 }}>{label}</p><p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>{sub}</p></div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section tabs */}
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { key: 'weather', label: '🌤 Weather Analytics', count: weatherData.length },
                { key: 'soil',    label: '🌱 Soil Analytics',    count: soilReadings.length },
              ].map(({ key, label, count }) => (
                <button key={key} onClick={() => setSection(key)} style={{
                  padding: '10px 18px', borderRadius: '10px 10px 0 0',
                  border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                  transition: 'all 0.2s',
                  background: section === key ? '#fff' : 'rgba(255,255,255,0.08)',
                  color: section === key ? '#0f172a' : 'rgba(255,255,255,0.65)',
                  boxShadow: section === key ? '0 -2px 12px rgba(0,0,0,0.1)' : 'none',
                  display: 'flex', alignItems: 'center', gap: 7,
                }}>
                  {label}
                  {count > 0 && section === key && (
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#ecfdf5', color: '#065f46', padding: '1px 6px', borderRadius: 10 }}>{count.toLocaleString()}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Date range + custom row */}
          <div style={{ background: '#fff', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', borderBottom: dateRange === 'custom' ? '1px solid #f1f5f9' : 'none' }}>
            <Calendar style={{ width: 14, height: 14, color: '#9ca3af', marginRight: 2, flexShrink: 0 }} />
            {RANGES.map(({ k, l }) => (
              <button key={k} onClick={() => setDateRange(k)} style={{
                padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: dateRange === k ? '#059669' : '#f3f4f6',
                color: dateRange === k ? '#fff' : '#374151',
                boxShadow: dateRange === k ? '0 2px 8px rgba(5,150,105,0.3)' : 'none',
              }}>{l}</button>
            ))}
          </div>

          {dateRange === 'custom' && (
            <div style={{ background: '#fafafa', padding: '10px 20px', display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              {[['From', customFrom, setCustomFrom], ['To', customTo, setCustomTo]].map(([lbl, val, setter]) => (
                <div key={lbl}>
                  <label style={{ display: 'block', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', marginBottom: 4 }}>{lbl}</label>
                  <input type="datetime-local" value={val} onChange={e => setter(e.target.value)} style={{ padding: '7px 11px', fontSize: 12, border: '1.5px solid #e5e7eb', borderRadius: 9, outline: 'none', background: '#fff' }} />
                </div>
              ))}
              <button onClick={handleCustomApply} style={{ padding: '7px 18px', background: '#059669', color: '#fff', border: 'none', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Apply</button>
            </div>
          )}
        </div>

        {/* ── WEATHER SECTION ──────────────────────────────────────────────── */}
        {section === 'weather' && (
          wLoading ? (
            <EmptyState icon={RefreshCw} title="Loading weather data…" accentColor="#10b981" />
          ) : wError ? (
            <EmptyState icon={AlertCircle} title={wError} onRetry={loadWeatherData} accentColor="#ef4444" />
          ) : weatherData.length === 0 ? (
            <EmptyState title="No weather data for this period" sub="Try a different date range or check station connectivity." />
          ) : (
            <>
              <div className="analytics-layout" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, alignItems: 'start' }}>
                {/* Stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {PARAMS.map(p => (
                    <StatCard key={p.key} param={p}
                      stat={getStatForParam(p, statistics)}
                      isSelected={selectedKeys.includes(p.key)}
                      onToggle={() => toggleParam(p.key)}
                    />
                  ))}
                </div>
                {/* Chart */}
                <ChartPanel
                  chartData={weatherChartData}
                  activeParams={weatherParams}
                  allParams={PARAMS}
                  chartType={chartType} setChartType={setChartType}
                  normalize={normalize} setNormalize={setNormalize}
                  statFn={weatherStatFn}
                  selectedKeys={selectedKeys}
                  onToggleParam={toggleParam}
                  dateRange={dateRange}
                  chartHeight={weatherChartH}
                />
              </div>
              <WeatherInsights st={statistics} count={weatherData.length} />
            </>
          )
        )}

        {/* ── SOIL SECTION ─────────────────────────────────────────────────── */}
        {section === 'soil' && (
          <>
            {/* Device selector */}
            {soilDevices.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #f1f5f9', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <Leaf style={{ width: 14, height: 14, color: '#10b981', flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>Sensor</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {soilDevices.map(d => {
                    const active = selectedSoilDev?.deviceId === d.deviceId;
                    return (
                      <button key={d.deviceId} onClick={() => setSelectedSoilDev(d)} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '6px 13px', borderRadius: 10, border: `1.5px solid ${active ? '#10b981' : '#e5e7eb'}`,
                        background: active ? '#ecfdf5' : '#f9fafb', cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, color: active ? '#065f46' : '#6b7280',
                        transition: 'all 0.15s', boxShadow: active ? '0 2px 8px rgba(16,185,129,0.2)' : 'none',
                      }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: d.status === 'active' ? '#10b981' : '#9ca3af', flexShrink: 0 }} />
                        {d.name || `Soil ${d.deviceId.slice(-6)}`}
                        {d.weatherStationId && <span style={{ fontSize: 10, color: active ? '#10b981' : '#d1d5db' }}>· {d.weatherStationId.slice(-6)}</span>}
                        {active && <ChevronRight style={{ width: 10, height: 10 }} />}
                      </button>
                    );
                  })}
                </div>
                {selectedSoilDev && (
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#9ca3af' }}>
                    Depth: {selectedSoilDev.depth_cm ?? 10} cm
                  </span>
                )}
              </div>
            )}

            {soilDevices.length === 0 ? (
              <EmptyState icon={Leaf} title="No soil sensors found" sub="Add soil sensors to your network or check your connection." accentColor="#10b981" />
            ) : !selectedSoilDev ? null
            : soilLoading ? (
              <EmptyState icon={RefreshCw} title="Loading soil data…" accentColor="#10b981" />
            ) : soilError ? (
              <EmptyState icon={AlertCircle} title={soilError} onRetry={loadSoilData} accentColor="#ef4444" />
            ) : soilReadings.length === 0 ? (
              <EmptyState icon={Leaf} title="No soil readings for this period" sub="Try a different date range." accentColor="#10b981" />
            ) : (
              <>
                <div className="analytics-layout" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, alignItems: 'start' }}>
                  {/* Soil stat cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {SOIL_PARAMS.map(p => (
                      <StatCard key={p.key} param={p}
                        stat={soilStats?.[p.key] ?? null}
                        isSelected={selectedSoilKeys.includes(p.key)}
                        onToggle={() => toggleSoilParam(p.key)}
                      />
                    ))}
                  </div>
                  {/* Soil chart */}
                  <ChartPanel
                    chartData={soilChartData}
                    activeParams={soilParams}
                    allParams={SOIL_PARAMS}
                    chartType={soilChartType} setChartType={setSoilChartType}
                    normalize={soilNormalize} setNormalize={setSoilNormalize}
                    statFn={soilStatFn}
                    selectedKeys={selectedSoilKeys}
                    onToggleParam={toggleSoilParam}
                    dateRange={dateRange}
                    chartHeight={soilChartH}
                    title="Soil Sensor Trends"
                    subtitle={`${RANGE_LABELS[dateRange]} · ${soilReadings.length} readings · ${selectedSoilDev?.name || selectedSoilDev?.deviceId}`}
                  />
                </div>
                <SoilInsights st={soilStats} count={soilReadings.length} />
              </>
            )}
          </>
        )}

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #f1f5f9', padding: '9px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            {section === 'weather' && weatherData.length > 0 && <>
              <span style={{ fontSize: 12, color: '#6b7280' }}><strong style={{ color: '#1f2937', fontWeight: 800 }}>{weatherData.length.toLocaleString()}</strong> readings</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}><strong style={{ color: '#1f2937', fontWeight: 800 }}>{weatherParams.length}</strong> param{weatherParams.length !== 1 ? 's' : ''}</span>
            </>}
            {section === 'soil' && soilReadings.length > 0 && <>
              <span style={{ fontSize: 12, color: '#6b7280' }}><strong style={{ color: '#1f2937', fontWeight: 800 }}>{soilReadings.length}</strong> soil readings</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}><strong style={{ color: '#1f2937', fontWeight: 800 }}>{soilParams.length}</strong> param{soilParams.length !== 1 ? 's' : ''}</span>
            </>}
            <span style={{ fontSize: 12, color: '#6b7280' }}><strong style={{ color: '#1f2937', fontWeight: 800 }}>{RANGE_LABELS[dateRange]}</strong></span>
          </div>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>Updated {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (min-width: 920px) {
          .analytics-layout { grid-template-columns: 320px 1fr !important; }
        }
        @media (max-width: 919px) {
          .analytics-layout { grid-template-columns: 1fr !important; }
          .analytics-outer { padding: 10px 10px 40px !important; }
        }
      `}</style>
    </div>
  );
};

export default WeatherAnalyticsDashboard;
